import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { toBusinessDateKey } from "@zapis24/utils";
import {
  DEFAULT_PUBLIC_PLATFORM_SUPPORT_SETTINGS,
} from "@zapis24/types/platform-settings-defaults";
import { apiFetch, toLocalDateStr, API_BASE } from "./api.js";
import { AppointmentsPage } from "./pages/AppointmentsPage.js";
import { ServicesPage } from "./pages/ServicesPage.js";
import { ClientsPage } from "./pages/ClientsPage.js";
import { SettingsPage } from "./pages/SettingsPage.js";
import { StatsPage } from "./pages/StatsPage.js";
import { ProfilePage } from "./pages/ProfilePage.js";
import { HomePage } from "./pages/HomePage.js";
import { SitePage } from "./pages/SitePage.js";
import { MarketplacePage } from "./pages/MarketplacePage.js";
import type {
  HomeAppointmentOpenTarget,
  HomeSettingsSection,
  HomeTargetTab,
} from "./homeDashboard.js";
import { getVisibleAppointmentCountFromStats } from "./appointmentsView.js";
import { readAuthHandoffFromUrl } from "./authToken.js";
import {
  logoutStoredAuthSession,
  readStoredAuthSession,
  saveAuthSession,
} from "./authSession.js";

type Tab =
  | "home"
  | "appointments"
  | "services"
  | "clients"
  | "stats"
  | "marketplace"
  | "site"
  | "settings"
  | "profile";

const TABS: { id: Tab; label: string; icon: string }[] = [
  { id: "home", label: "Главная", icon: "⌂" },
  { id: "appointments", label: "Расписание", icon: "📅" },
  { id: "services", label: "Услуги", icon: "💼" },
  { id: "clients", label: "Клиенты", icon: "👥" },
  { id: "stats", label: "Статистика", icon: "📊" },
  { id: "marketplace", label: "Выгодно", icon: "🏷️" },
  { id: "site", label: "Сайт", icon: "🌐" },
  { id: "settings", label: "Настройки", icon: "⚙️" },
  { id: "profile", label: "Ссылки", icon: "🔗" },
];

const MOBILE_PRIMARY_TAB_IDS: Tab[] = [
  "home",
  "appointments",
  "stats",
  "profile",
];
const MOBILE_MORE_TAB_IDS: Tab[] = [
  "marketplace",
  "services",
  "clients",
  "site",
  "settings",
];

function getTabMeta(id: Tab) {
  return TABS.find((tab) => tab.id === id)!;
}

type SupportRequestPayload = {
  category: string;
  contactMethod: string;
  page: Tab;
  message: string;
};
type OpenTabOptions = { settingsSection?: HomeSettingsSection | undefined };

export function App() {
  const [token, setToken] = useState<string | null>(() => {
    const urlToken = readAuthHandoffFromUrl(window.location);
    if (urlToken.accessToken) {
      saveAuthSession({
        accessToken: urlToken.accessToken,
        refreshToken: urlToken.refreshToken,
      });
      window.history.replaceState(
        {},
        "",
        urlToken.cleanUrl ?? window.location.pathname,
      );
      return urlToken.accessToken;
    }
    return readStoredAuthSession()?.accessToken ?? null;
  });

  useEffect(() => {
    function handleTokenRefresh(event: Event) {
      const detail = (event as CustomEvent<{ accessToken?: string }>).detail;
      if (detail?.accessToken) setToken(detail.accessToken);
    }

    window.addEventListener("admin-auth-token-refreshed", handleTokenRefresh);
    return () =>
      window.removeEventListener(
        "admin-auth-token-refreshed",
        handleTokenRefresh,
      );
  }, []);

  function handleLogin(session: { accessToken: string; refreshToken: string }) {
    saveAuthSession(session);
    setToken(session.accessToken);
  }

  function handleLogout() {
    void logoutStoredAuthSession(API_BASE);
    setToken(null);
  }

  if (!token) {
    return <LoginPage onLogin={handleLogin} />;
  }

  return <Dashboard token={token} onLogout={handleLogout} />;
}

// ─── Login ───────────────────────────────────────────────────────────────────

function LoginPage({
  onLogin,
}: {
  onLogin: (session: { accessToken: string; refreshToken: string }) => void;
}) {
  type PasswordResetStep = "login" | "request" | "code" | "newPassword" | "done";

  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [passwordResetStep, setPasswordResetStep] =
    useState<PasswordResetStep>("login");
  const [passwordResetCode, setPasswordResetCode] = useState("");
  const [passwordResetToken, setPasswordResetToken] = useState("");
  const [passwordResetNewPassword, setPasswordResetNewPassword] = useState("");
  const [passwordResetConfirmPassword, setPasswordResetConfirmPassword] =
    useState("");
  const [passwordResetNotice, setPasswordResetNotice] = useState("");

  async function handleLoginSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, password }),
      });
      const data = (await res.json()) as {
        ok: boolean;
        data?: { accessToken: string; refreshToken: string };
        error?: string;
      };
      if (!data.ok || !data.data) throw new Error(data.error ?? "Ошибка входа");
      onLogin({
        accessToken: data.data.accessToken,
        refreshToken: data.data.refreshToken,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Ошибка входа");
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordResetRequest(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setPasswordResetNotice("");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/password-reset/request`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone }),
      });
      const data = (await res.json()) as { ok: boolean; error?: string };
      if (!data.ok) throw new Error(data.error ?? "Не удалось отправить код");
      setPasswordResetCode("");
      setPasswordResetNotice(
        "Если кабинет с таким телефоном найден и бот привязан, код уже отправлен.",
      );
      setPasswordResetStep("code");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось отправить код");
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordResetVerify(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/api/auth/password-reset/verify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ phone, code: passwordResetCode }),
      });
      const data = (await res.json()) as {
        ok: boolean;
        data?: { resetToken: string };
        error?: string;
      };
      if (!data.ok || !data.data) {
        throw new Error(data.error ?? "Неверный или истекший код");
      }
      setPasswordResetToken(data.data.resetToken);
      setPasswordResetNewPassword("");
      setPasswordResetConfirmPassword("");
      setPasswordResetStep("newPassword");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Неверный или истекший код");
    } finally {
      setLoading(false);
    }
  }

  async function handlePasswordResetConfirm(e: React.FormEvent) {
    e.preventDefault();
    setError("");
    if (passwordResetNewPassword !== passwordResetConfirmPassword) {
      setError("Пароли не совпадают");
      return;
    }

    setLoading(true);
    try {
      const resetToken = passwordResetToken;
      const res = await fetch(`${API_BASE}/api/auth/password-reset/confirm`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ resetToken, newPassword: passwordResetNewPassword }),
      });
      const data = (await res.json()) as { ok: boolean; error?: string };
      if (!data.ok) throw new Error(data.error ?? "Не удалось обновить пароль");
      setPassword("");
      setPasswordResetStep("done");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Не удалось обновить пароль");
    } finally {
      setLoading(false);
    }
  }

  function returnToLogin() {
    setError("");
    setLoading(false);
    setPasswordResetStep("login");
    setPasswordResetCode("");
    setPasswordResetToken("");
    setPasswordResetNewPassword("");
    setPasswordResetConfirmPassword("");
    setPasswordResetNotice("");
  }

  function supportFallback() {
    return (
      <div className="rounded-xl border border-primary/10 bg-primary/5 px-3 py-3 text-xs leading-5 text-gray-600">
        <p className="font-semibold text-gray-800">Если код не пришел</p>
        <p className="mt-1">
          Напишите в поддержку и укажите телефон от кабинета. Мы проверим
          доступ и поможем восстановить пароль.
        </p>
        <div className="mt-3 flex flex-wrap gap-2">
          <a
            href={DEFAULT_PUBLIC_PLATFORM_SUPPORT_SETTINGS.supportLinks.telegramUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-9 items-center rounded-lg bg-white px-3 py-2 font-semibold text-primary ring-1 ring-primary/20 hover:ring-primary/40"
          >
            Telegram
          </a>
          <a
            href={DEFAULT_PUBLIC_PLATFORM_SUPPORT_SETTINGS.supportLinks.maxUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex min-h-9 items-center rounded-lg bg-white px-3 py-2 font-semibold text-primary ring-1 ring-primary/20 hover:ring-primary/40"
          >
            MAX
          </a>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
      <div className="w-full max-w-sm">
        <div className="text-center mb-8">
          <img
            src="/brand/zapis24-logo.svg"
            alt="Запись24"
            className="h-16 w-auto mx-auto mb-4 object-contain"
          />
          <h1 className="sr-only">Запись24</h1>
          <p className="text-gray-400 text-sm mt-1">
            Панель управления мастера
          </p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          {passwordResetStep === "login" && (
            <form onSubmit={handleLoginSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="login-phone"
                  className="text-xs text-gray-500 mb-1 block"
                >
                  Телефон
                </label>
                <input
                  id="login-phone"
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary"
                  placeholder="+7 900 000 00 00"
                  autoComplete="tel"
                  inputMode="tel"
                />
              </div>
              <div>
                <div className="mb-1 flex items-center justify-between gap-3">
                  <label
                    htmlFor="login-password"
                    className="block text-xs text-gray-500"
                  >
                    Пароль
                  </label>
                  <button
                    type="button"
                    onClick={() => {
                      setError("");
                      setPasswordResetStep("request");
                    }}
                    className="text-xs font-semibold text-primary hover:text-primary/80"
                  >
                    Забыли пароль?
                  </button>
                </div>
                <input
                  id="login-password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary"
                  placeholder="••••••••"
                  autoComplete="current-password"
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm bg-red-50 rounded-xl px-3 py-2">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-white rounded-xl py-2.5 font-semibold hover:opacity-90 transition disabled:opacity-50"
              >
                {loading ? "Вход..." : "Войти"}
              </button>
            </form>
          )}

          {passwordResetStep === "request" && (
            <form onSubmit={handlePasswordResetRequest} className="space-y-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Введите телефон
                </h2>
                <p className="mt-1 text-sm leading-5 text-gray-500">
                  Мы отправим код в Telegram или MAX, который привязан к
                  кабинету мастера.
                </p>
              </div>
              <div>
                <label
                  htmlFor="password-reset-phone"
                  className="text-xs text-gray-500 mb-1 block"
                >
                  Телефон от кабинета
                </label>
                <input
                  id="password-reset-phone"
                  type="tel"
                  required
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary"
                  placeholder="+7 900 000 00 00"
                  autoComplete="tel"
                  inputMode="tel"
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm bg-red-50 rounded-xl px-3 py-2">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-white rounded-xl py-2.5 font-semibold hover:opacity-90 transition disabled:opacity-50"
              >
                {loading ? "Отправляем..." : "Отправить код"}
              </button>
              <button
                type="button"
                onClick={returnToLogin}
                className="w-full rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-600 hover:border-gray-300"
              >
                Вернуться ко входу
              </button>
              {supportFallback()}
            </form>
          )}

          {passwordResetStep === "code" && (
            <form onSubmit={handlePasswordResetVerify} className="space-y-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Код из бота
                </h2>
                <p className="mt-1 text-sm leading-5 text-gray-500">
                  Введите шесть цифр из сообщения. Код действует 10 минут.
                </p>
              </div>

              {passwordResetNotice && (
                <p className="rounded-xl bg-green-50 px-3 py-2 text-sm text-green-700">
                  {passwordResetNotice}
                </p>
              )}

              <div>
                <label
                  htmlFor="password-reset-code"
                  className="text-xs text-gray-500 mb-1 block"
                >
                  Код подтверждения
                </label>
                <input
                  id="password-reset-code"
                  type="text"
                  required
                  value={passwordResetCode}
                  onChange={(e) =>
                    setPasswordResetCode(e.target.value.replace(/\D/g, "").slice(0, 6))
                  }
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-center text-lg font-semibold tracking-[0.2em] focus:outline-none focus:border-primary"
                  placeholder="000000"
                  autoComplete="one-time-code"
                  inputMode="numeric"
                  minLength={6}
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm bg-red-50 rounded-xl px-3 py-2">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading || passwordResetCode.length !== 6}
                className="w-full bg-primary text-white rounded-xl py-2.5 font-semibold hover:opacity-90 transition disabled:opacity-50"
              >
                {loading ? "Проверяем..." : "Проверить код"}
              </button>
              <button
                type="button"
                onClick={() => {
                  setError("");
                  setPasswordResetStep("request");
                }}
                className="w-full rounded-xl border border-gray-200 py-2.5 text-sm font-semibold text-gray-600 hover:border-gray-300"
              >
                Отправить код еще раз
              </button>
              {supportFallback()}
            </form>
          )}

          {passwordResetStep === "newPassword" && (
            <form onSubmit={handlePasswordResetConfirm} className="space-y-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Новый пароль
                </h2>
                <p className="mt-1 text-sm leading-5 text-gray-500">
                  Пароль должен быть не короче 8 символов.
                </p>
              </div>
              <div>
                <label
                  htmlFor="password-reset-new-password"
                  className="text-xs text-gray-500 mb-1 block"
                >
                  Новый пароль
                </label>
                <input
                  id="password-reset-new-password"
                  type="password"
                  required
                  minLength={8}
                  value={passwordResetNewPassword}
                  onChange={(e) => setPasswordResetNewPassword(e.target.value)}
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary"
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>
              <div>
                <label
                  htmlFor="password-reset-confirm-password"
                  className="text-xs text-gray-500 mb-1 block"
                >
                  Повторите пароль
                </label>
                <input
                  id="password-reset-confirm-password"
                  type="password"
                  required
                  minLength={8}
                  value={passwordResetConfirmPassword}
                  onChange={(e) =>
                    setPasswordResetConfirmPassword(e.target.value)
                  }
                  className="w-full border border-gray-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:border-primary"
                  placeholder="••••••••"
                  autoComplete="new-password"
                />
              </div>

              {error && (
                <p className="text-red-500 text-sm bg-red-50 rounded-xl px-3 py-2">
                  {error}
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-primary text-white rounded-xl py-2.5 font-semibold hover:opacity-90 transition disabled:opacity-50"
              >
                {loading ? "Сохраняем..." : "Сохранить пароль"}
              </button>
            </form>
          )}

          {passwordResetStep === "done" && (
            <div className="space-y-4">
              <div>
                <h2 className="text-base font-semibold text-gray-900">
                  Пароль обновлен
                </h2>
                <p className="mt-1 text-sm leading-5 text-gray-500">
                  Теперь можно войти в кабинет с новым паролем.
                </p>
              </div>
              <button
                type="button"
                onClick={returnToLogin}
                className="w-full bg-primary text-white rounded-xl py-2.5 font-semibold hover:opacity-90 transition"
              >
                Войти
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Dashboard ───────────────────────────────────────────────────────────────

function Dashboard({
  token,
  onLogout,
}: {
  token: string;
  onLogout: () => void;
}) {
  const [activeTab, setActiveTab] = useState<Tab>(() => {
    const requestedTab = new URLSearchParams(window.location.search).get("tab");
    return TABS.some((tab) => tab.id === requestedTab)
      ? (requestedTab as Tab)
      : "home";
  });
  const [isMoreOpen, setIsMoreOpen] = useState(false);
  const [isSupportOpen, setIsSupportOpen] = useState(false);
  const [appointmentFocus, setAppointmentFocus] = useState<{
    appointmentId: string;
    date: string;
    requestId: number;
  } | null>(null);
  const [settingsFocus, setSettingsFocus] = useState<{
    section: HomeSettingsSection;
    requestId: number;
  } | null>(null);

  function openTab(tab: Tab, options: OpenTabOptions = {}) {
    setActiveTab(tab);
    setIsMoreOpen(false);
    if (tab !== "appointments") setAppointmentFocus(null);
    if (tab === "settings" && options.settingsSection) {
      setSettingsFocus({
        section: options.settingsSection,
        requestId: Date.now(),
      });
    } else if (tab !== "settings" || !options.settingsSection) {
      setSettingsFocus(null);
    }
  }

  function openAppointment(target: HomeAppointmentOpenTarget) {
    setAppointmentFocus({
      appointmentId: target.appointmentId,
      date: target.date,
      requestId: Date.now(),
    });
    openTab("appointments");
  }

  const { data: business } = useQuery({
    queryKey: ["business-me"],
    queryFn: () =>
      apiFetch<{
        name: string;
        brandColor: string | null;
        timeZone: string;
      }>(token, "/api/business/me"),
    staleTime: 300_000,
  });
  const today = business?.timeZone ? toBusinessDateKey(new Date(), business.timeZone) : toLocalDateStr(new Date());

  const { data: todayStats } = useQuery({
    queryKey: ["today-stats"],
    queryFn: () =>
      apiFetch<{ total: number; active: number; cancelled: number; noShow: number }>(
        token,
        `/api/appointments/stats?date=${today}`,
      ),
    staleTime: 30_000,
  });

  const todayCount = getVisibleAppointmentCountFromStats(todayStats);
  const isMobileMoreActive = MOBILE_MORE_TAB_IDS.includes(activeTab);
  const activeMobileMoreTab = isMobileMoreActive ? getTabMeta(activeTab) : null;

  return (
    <div className="admin-shell flex h-dvh min-h-dvh w-full max-w-[100vw] min-w-0 flex-col overflow-hidden bg-gray-50">
      {/* Header */}
      <header className="sticky top-0 z-30 shrink-0 border-b border-gray-200 bg-white px-4 py-3">
        <div className="mx-auto flex w-full max-w-2xl min-w-0 items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <div
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg text-sm font-bold text-white"
              style={{ backgroundColor: business?.brandColor ?? "#B83268" }}
            >
              {(business?.name ?? "З").charAt(0).toUpperCase()}
            </div>
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold leading-none text-gray-800">
                {business?.name ?? "Запись24"}
              </p>
              <p className="mt-0.5 truncate text-[11px] text-gray-400">
                {(activeTab === "home" || activeTab === "appointments") &&
                todayCount > 0
                  ? `📅 Сегодня: ${todayCount} ${todayCount === 1 ? "запись" : todayCount < 5 ? "записи" : "записей"}`
                  : "Панель управления"}
              </p>
            </div>
          </div>
          <div className="flex shrink-0 items-center gap-1">
            <button
              onClick={() => setIsSupportOpen(true)}
              className="rounded-lg border border-primary/15 bg-primary/5 px-2.5 py-1.5 text-xs font-semibold text-primary"
            >
              Помощь
            </button>
            <button
              onClick={() => {
                if (confirm("Выйти из системы?")) onLogout();
              }}
              className="px-2 py-1 text-xs text-gray-400 transition hover:text-gray-700"
            >
              Выйти
            </button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="admin-main min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden">
        {activeTab === "home" && (
          <HomePage
            token={token}
            onOpenTab={(tab: HomeTargetTab) => openTab(tab)}
            onOpenAppointment={openAppointment}
          />
        )}
        {activeTab === "appointments" && (
          <AppointmentsPage
            token={token}
            initialDate={appointmentFocus?.date}
            focusAppointmentId={appointmentFocus?.appointmentId}
            focusRequestId={appointmentFocus?.requestId}
          />
        )}
        {activeTab === "services" && <ServicesPage token={token} />}
        {activeTab === "clients" && <ClientsPage token={token} />}
        {activeTab === "stats" && <StatsPage token={token} />}
        {activeTab === "marketplace" && <MarketplacePage token={token} />}
        {activeTab === "site" && <SitePage token={token} onOpenSupport={() => setIsSupportOpen(true)} />}
        {activeTab === "settings" && (
          <SettingsPage
            token={token}
            focusSection={settingsFocus?.section}
            focusRequestId={settingsFocus?.requestId}
          />
        )}
        {activeTab === "profile" && (
          <ProfilePage token={token} onOpenSite={() => openTab("site")} />
        )}
      </main>

      <SupportHelpDialog
        open={isSupportOpen}
        token={token}
        page={activeTab}
        settings={DEFAULT_PUBLIC_PLATFORM_SUPPORT_SETTINGS}
        onClose={() => setIsSupportOpen(false)}
      />

      {/* Bottom navigation */}
      {isMoreOpen && (
        <div
          className="fixed inset-0 z-50 bg-gray-950/25 sm:hidden"
          onClick={() => setIsMoreOpen(false)}
        >
          <div
            className="admin-more-menu-panel absolute inset-x-3 rounded-2xl border border-gray-100 bg-white p-2 shadow-2xl"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="grid gap-1">
              {MOBILE_MORE_TAB_IDS.map((tabId) => {
                const tab = getTabMeta(tabId);
                return (
                  <button
                    key={tab.id}
                    type="button"
                    onClick={() => openTab(tab.id)}
                    aria-current={activeTab === tab.id ? "page" : undefined}
                    className={`flex min-w-0 items-center gap-3 rounded-xl px-3 py-3 text-left transition ${
                      activeTab === tab.id
                        ? "bg-primary text-white shadow-sm"
                        : "text-gray-600 hover:bg-gray-50 hover:text-gray-800"
                    }`}
                  >
                    <span className="w-7 shrink-0 text-center text-lg leading-none">
                      {tab.icon}
                    </span>
                    <span className="min-w-0 truncate text-sm font-semibold">
                      {tab.label}
                    </span>
                    {activeTab === tab.id && (
                      <span className="ml-auto h-2 w-2 shrink-0 rounded-full bg-white/90" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      <nav className="admin-desktop-bottom-nav hidden shrink-0 border-t border-gray-100 bg-white/95 px-3 py-2 shadow-[0_-10px_30px_rgba(15,23,42,0.08)] backdrop-blur sm:block">
        <div className="mx-auto flex w-full max-w-2xl gap-1">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => openTab(tab.id)}
              aria-current={activeTab === tab.id ? "page" : undefined}
              className={`relative flex min-w-[64px] flex-1 flex-col items-center gap-1 rounded-2xl px-1 py-2 transition ${
                activeTab === tab.id
                  ? "bg-primary/10 text-primary shadow-sm ring-1 ring-primary/15"
                  : "text-gray-400 hover:bg-gray-50 hover:text-gray-700"
              }`}
            >
              <span
                className={`relative flex h-7 w-7 items-center justify-center rounded-xl text-lg leading-none transition ${
                  activeTab === tab.id
                    ? "bg-white shadow-[0_1px_4px_rgba(184,50,104,0.24)]"
                    : ""
                }`}
              >
                {tab.icon}
                {tab.id === "appointments" && todayCount > 0 && (
                  <span className="absolute -right-2 -top-1 flex h-4 w-4 items-center justify-center rounded-full bg-red-500 text-[9px] font-bold leading-none text-white">
                    {todayCount > 9 ? "9+" : todayCount}
                  </span>
                )}
              </span>
              <span
                className={`text-[10px] leading-none ${
                  activeTab === tab.id ? "font-bold" : "font-medium"
                }`}
              >
                {tab.label}
              </span>
            </button>
          ))}
        </div>
      </nav>

      <nav className="admin-bottom-nav admin-mobile-bottom-nav fixed bottom-0 left-0 right-0 z-40 grid grid-cols-5 gap-1 border-t border-gray-100 bg-white/95 px-2 pb-[calc(0.5rem+env(safe-area-inset-bottom))] pt-2 shadow-[0_-10px_30px_rgba(15,23,42,0.1)] backdrop-blur sm:hidden">
        {MOBILE_PRIMARY_TAB_IDS.map((tabId) => {
          const tab = getTabMeta(tabId);
          return (
            <MobileTabButton
              key={tab.id}
              tab={tab}
              active={activeTab === tab.id}
              onClick={() => openTab(tab.id)}
            />
          );
        })}
        <button
          type="button"
          onClick={() => setIsMoreOpen((current) => !current)}
          className={`flex min-w-0 flex-col items-center gap-1 rounded-2xl px-1 py-2 transition ${
            isMobileMoreActive || isMoreOpen
              ? "bg-primary/10 text-primary shadow-sm ring-1 ring-primary/15"
              : "text-gray-400 hover:bg-gray-50 hover:text-gray-600"
          }`}
          aria-expanded={isMoreOpen}
          aria-current={isMobileMoreActive ? "page" : undefined}
          aria-label={
            activeMobileMoreTab
              ? `Открыть остальные разделы, текущий раздел: ${activeMobileMoreTab.label}`
              : "Открыть остальные разделы"
          }
        >
          <span
            className={`flex h-7 w-7 items-center justify-center rounded-xl text-lg leading-none transition ${
              isMobileMoreActive || isMoreOpen
                ? "bg-white shadow-[0_1px_4px_rgba(184,50,104,0.24)]"
                : ""
            }`}
          >
            ⋯
          </span>
          <span
            className={`max-w-full truncate text-[10px] leading-none ${
              isMobileMoreActive || isMoreOpen ? "font-bold" : "font-medium"
            }`}
          >
            Ещё
          </span>
        </button>
      </nav>
    </div>
  );
}

function SupportHelpDialog({
  open,
  token,
  page,
  settings,
  onClose,
}: {
  open: boolean;
  token: string;
  page: Tab;
  settings: typeof DEFAULT_PUBLIC_PLATFORM_SUPPORT_SETTINGS;
  onClose: () => void;
}) {
  const supportMutation = useMutation({
    mutationFn: (body: SupportRequestPayload) =>
      apiFetch<{ requested: boolean }>(token, "/api/business/me/support-requests", {
        method: "POST",
        body: JSON.stringify(body),
      }),
  });

  if (!open) return null;

  function submitSupportRequest(event: React.FormEvent) {
    event.preventDefault();
    const form = event.currentTarget as HTMLFormElement;
    const data = new FormData(form);
    supportMutation.mutate(
      {
        category: "OTHER",
        contactMethod: "CALLBACK",
        page,
        message: String(data.get("message") ?? "").trim(),
      },
      { onSuccess: () => form.reset() },
    );
  }

  return (
    <div
      className="support-backdrop"
      role="dialog"
      onClick={onClose}
    >
        <div
          className="support-dialog"
          onClick={(event) => event.stopPropagation()}
        >
          <div className="support-dialog-header">
            <div>
              <h2 className="support-title">
                Помощь
              </h2>
            </div>
            <button
              onClick={onClose}
              aria-label="Закрыть"
              className="support-close"
            >
              ×
            </button>
          </div>

        <div className="support-direct-grid">
          <a
            href={settings.supportLinks.telegramUrl}
            target="_blank"
            rel="noreferrer"
            className="support-direct-link"
          >
            Telegram
          </a>
          <a
            href={settings.supportLinks.maxUrl}
            target="_blank"
            rel="noreferrer"
            className="support-direct-link"
          >
            MAX
          </a>
          <a
            href={`tel:${settings.legalDetails.phone.replace(/\s/g, "")}`}
            className="support-direct-link"
          >
            Позвонить
          </a>
        </div>

        <form onSubmit={submitSupportRequest} className="support-form">
          <label className="block">
            <span className="support-section-label">
              Обратный звонок
            </span>
            <textarea
              name="message"
              rows={4}
              maxLength={1000}
              className="support-textarea"
            />
          </label>

          <button
            disabled={supportMutation.isPending}
            className="support-submit"
          >
            {supportMutation.isPending
              ? "Отправляем..."
              : supportMutation.isSuccess
                ? "Отправлено"
                : supportMutation.isError
                  ? "Не отправлено"
                  : "Отправить заявку"}
          </button>
        </form>
      </div>
    </div>
  );
}

function MobileTabButton({
  tab,
  active,
  onClick,
}: {
  tab: { id: Tab; label: string; icon: string };
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={active ? "page" : undefined}
      className={`relative flex min-w-0 flex-col items-center gap-1 rounded-2xl px-1 py-2 transition ${
        active
          ? "bg-primary/10 text-primary shadow-sm ring-1 ring-primary/15"
          : "text-gray-400 hover:bg-gray-50 hover:text-gray-600"
      }`}
    >
      <span
        className={`flex h-7 w-7 items-center justify-center rounded-xl text-lg leading-none transition ${
          active ? "bg-white shadow-[0_1px_4px_rgba(184,50,104,0.24)]" : ""
        }`}
      >
        {tab.icon}
      </span>
      <span
        className={`max-w-full truncate text-[10px] leading-none ${
          active ? "font-bold" : "font-medium"
        }`}
      >
        {tab.label}
      </span>
    </button>
  );
}
